import React from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Flex,
  FlexProps,
  Heading,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleRight } from '@fortawesome/pro-regular-svg-icons';

import { SectionStatusType } from 'types';

import { RiskIcon } from '../RiskAction/RiskIcon';
import { RiskAlert } from '../RiskAction/RiskAlert';
import { Span } from 'shared-components';

interface ISection extends React.HTMLProps<HTMLDivElement> {
  id?: string;
  label?: string;
  amount?: string;
  warning?: React.ReactElement | string | null;
  metaData?: React.ReactElement;
  minMetaDataHeight?: number;
  childen?: React.ReactNode;
  sectionStatus?: SectionStatusType;
  canEdit?: boolean;
  canCheckOff?: boolean;
  edit?: () => void;
  isRisk?: boolean;
  isRiskSection?: boolean;
  isCheckedOff?: () => boolean;
  onCheckOffRisk?: () => void;
  onChangeStatusClick?: (status: string) => void;
}

const Section: React.FC<ISection> = (props: ISection) => {
  const {
    id = 'section',
    label,
    warning,
    isRiskSection,
    children,
    canEdit = false,
    edit,
    amount,
    metaData,
    canCheckOff,
    isCheckedOff,
    onCheckOffRisk,
  } = props;

  const { isOpen, onToggle } = useDisclosure();

  const getClickableProps = () => {
    if (metaData) {
      return {
        'aria-label': 'Details',
        'data-selector': `${id}-metadata-switch`,
        variant: 'unstyled',
        as: Button,
        onClick: onToggle,
        _hover: { bgColor: 'hover-bg' },
        _focusVisible: { bgColor: 'hover-bg' },
      };
    }
    return null;
  };

  const xSpacing: FlexProps['px'] = { base: 3, xl: 6 };

  return (
    <Box data-selector={id} bgColor={'chakra-subtle-bg'} mb={10}>
      <Flex data-selector={`${id}-title`} py={4} px={xSpacing}>
        {label && (
          <Heading as="h3" fontSize={'lg'} fontWeight={'medium'} data-selector={`${id}-label`} lineHeight={6} py={2}>
            {label}
          </Heading>
        )}

        <Flex alignItems={'center'} ml={'auto'} gap={6}>
          <Flex pl={1} pr={3} py={1} borderRadius={'lg'} {...getClickableProps()}>
            {metaData && (
              <Flex width={7} alignItems={'center'}>
                <FontAwesomeIcon
                  icon={isOpen ? faAngleDown : faAngleRight}
                  size="lg"
                  className="utility-icon"
                  fixedWidth
                />
              </Flex>
            )}

            {amount && (
              <Box data-selector={`${id}-amount`} fontSize={'lg'} lineHeight={6} fontWeight={'medium'}>
                {amount}
              </Box>
            )}

            {isRiskSection ? (
              <Span data-selector={`${id}-risk-status-icon`}>
                <RiskIcon
                  id={id}
                  canCheckOff={canCheckOff}
                  onCheckOffRisk={onCheckOffRisk}
                  isCheckedOff={isCheckedOff && isCheckedOff()}
                  boxProps={{ ml: 2 }}
                />
              </Span>
            ) : null}
          </Flex>

          {canEdit && (
            <Button variant={'secondary'} data-selector={`section-edit`} onClick={edit}>
              Edit
            </Button>
          )}
        </Flex>
      </Flex>

      {metaData && (
        <Collapse in={isOpen}>
          {isRiskSection ? (
            <RiskAlert
              id={id}
              canCheckOff={canCheckOff}
              onCheckOffRisk={onCheckOffRisk}
              isCheckedOff={isCheckedOff && isCheckedOff()}
              boxProps={{ mt: 0, mx: 6, bgColor: 'utility.100', _dark: { bgColor: 'whiteAlpha.50' } }}
            />
          ) : null}

          <Box data-selector={`${id}-description`} px={xSpacing} my={2}>
            {metaData}
          </Box>
        </Collapse>
      )}

      {warning && (
        <Alert status="warning" data-selector={`${id}-warning`} mx={xSpacing} mb={4} width={'auto'}>
          <AlertIcon />
          {warning}
        </Alert>
      )}

      <Stack spacing={0}>{children}</Stack>
    </Box>
  );
};

export { Section };
